import type { SkeletonProgressData } from "@client/hooks/use-my-evaluations-data";
import { formatAsPercentage } from "@client/utils/formatPercentage";
import { useEffect, useState } from "react";

export function ProposalsProgressCard({
  completed,
  total,
  milestoneLabel,
  targetPercent,
  targetLabel,
}: SkeletonProgressData) {
  const hasData = completed != null && total != null;
  const ratio = hasData && total > 0 ? completed / total : 0;
  const percentValue = ratio * 100;

  // Animate the bar from 0 on mount
  const [animatedWidth, setAnimatedWidth] = useState(0);
  useEffect(() => {
    // Delay one frame so the browser paints at 0% first, then the CSS
    // transition animates to the real value.
    const id = requestAnimationFrame(() => setAnimatedWidth(percentValue));
    return () => cancelAnimationFrame(id);
  }, [percentValue]);

  return (
    <div className="rounded-xl border border-border-subtle bg-surface-elevated px-5 py-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-body-lg font-medium text-foreground">
          {hasData ? `${completed}/${total}` : "—/—"} evaluations started
        </span>
        {milestoneLabel && (
          <span className="shrink-0 rounded-md border border-accent bg-accent-muted px-2.5 py-1 text-body-sm font-medium text-accent">
            {milestoneLabel}
          </span>
        )}
      </div>

      <div className="mt-3 flex flex-col gap-1.5">
        <div className="relative h-2 w-full overflow-hidden rounded-full bg-surface-hover">
          <div
            className="h-full rounded-full bg-success transition-[width] duration-700 ease-out"
            style={{ width: `${animatedWidth}%` }}
          />
          {targetPercent != null &&
            targetPercent > 0 &&
            targetPercent < 100 && (
              <div
                className="absolute top-0 h-full w-0.5 bg-border-strong"
                style={{ left: `${targetPercent}%` }}
                title={`Target: ${targetPercent}%`}
              />
            )}
        </div>
        <div className="flex justify-between text-body-sm text-muted-foreground">
          <span>{hasData ? formatAsPercentage(ratio) : "—"}</span>
          {targetLabel != null && targetPercent != null && (
            <span>
              TARGET = {targetPercent}% • {targetLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
